/**
 * Hisobot registri — **metadata qismi** (spec §4.1, kontrakt §3.1).
 *
 * ⚠️ **SQL bu yerda YO'Q.** `packages/domain` ning yagona tashqi
 * bog'lanishi `zod`; `drizzle-orm` qo'shilmaydi. So'rov quruvchilari
 * `apps/backend/src/routes/reports/queries/` da va ular bu registrga
 * `satisfies Record<ReportKind, ReportQuery>` bilan bog'lanadi — yangi
 * descriptor qo'shilib so'rovi yozilmasa `bun run typecheck` qizil
 * bo'ladi.
 *
 * ⚠️ **Descriptor kaliti ≠ DB ustuni.** Masalan `tasks` descriptorining
 * `title` kaliti `tasks.name` dan, `dueAt` esa `tasks.dueTo` dan
 * olinadi (kontrakt §2.2). Kalitlar foydalanuvchiga ko'rinadigan shakl,
 * ustunlar esa saqlash shakli — ular ATAYLAB ajratilgan va kalitlar
 * o'zgarmaydi (kontrakt o'zgarishi).
 *
 * ⚠️ **Zod cheklovi `form.ts` dagi bilan bir xil:** `.catch()`,
 * `z.lazy()`, `z.union()`, `.transform()` ISHLATILMAYDI va `.openapi()`
 * CHAQIRILMAYDI — bu sxemalar `/doc` OpenAPI konvertoridan o'tadi.
 *
 * ⚠️ `zod/v4` specifieri — `form.ts` va `shared` bilan AYNAN bir xil.
 */
import { z } from "zod/v4";

export type ReportColumn = {
	key: string;
	title: string;
	type: "text" | "number" | "date" | "datetime" | "duration" | "boolean";
	/** XLSX ustun kengligi (belgilarda). */
	width?: number;
};

/**
 * Doira qaysi o'lchov bo'yicha toraytiriladi.
 *
 * ⚠️ `zone` o'lchovi ATAYLAB YO'Q: `zones` doira filtri
 * `department_id` bo'yicha ishlaydi va hisobot qatorlari xodimga yoki
 * bo'limga tegishli. Hudud — filtr, doira o'lchovi emas.
 */
export type ReportScopeDimension = "employee" | "department";

/**
 * To'qqiz hisobot kaliti — **QAT'IY** (kontrakt §3.1). Yangi hisobot
 * qo'shish kontrakt o'zgarishi.
 *
 * ⚠️ Reja bu birlashmani registrdan hosila qilishni taklif qilgan
 * (`keyof typeof REPORT_DESCRIPTORS` + `as const satisfies`), lekin u
 * TS2456 «circularly references itself» bilan yiqiladi: registr
 * `ReportDescriptor` ga tayanadi, `ReportDescriptor.kind` esa
 * `ReportKind` ga — halqa yopiladi va `REPORT_DESCRIPTORS` jimgina
 * `any` bo'lardi. Shuning uchun birlashma QO'LDA yoziladi, kontraktdagi
 * imzo bilan aynan bir xil.
 *
 * ⚠️ Ro'yxat ikki joyda bo'lsa-da, ular AJRALA OLMAYDI:
 * `Record<ReportKind, ReportDescriptor>` yetishmayotgan kalitni ham,
 * ortiqcha kalitni ham `bun run typecheck` da qizil qiladi.
 */
export type ReportKind =
	| "attendance_daily"
	| "attendance_monthly"
	| "lateness"
	| "zone_exits"
	| "fake_location"
	| "work_time"
	| "violations"
	| "tasks"
	| "leads";

export type ReportDescriptor = {
	kind: ReportKind;
	title: string;
	description: string;
	scopeDimension: ReportScopeDimension;
	filters: z.ZodObject<z.ZodRawShape>;
	columns: ReportColumn[];
	/**
	 * Eksportning yuqori chegarasi. Oshsa job `failed` bo'ladi va
	 * foydalanuvchi o'zbekcha xabar oladi.
	 */
	maxRows: number;
};

/**
 * Sana oralig'i — HAR hisobotda MAJBURIY.
 *
 * ⚠️ Chegarasiz oraliq `attendance_days` ni to'liq skan qilardi.
 * Yuqori chegara `report_max_range_days` sozlamasi bilan **backend
 * tomonida** tekshiriladi (bu yerda emas: `domain` sozlamalarni
 * o'qimaydi).
 */
const sanaSchema = z
	.string()
	.regex(/^\d{4}-\d{2}-\d{2}$/, "Sana YYYY-MM-DD shaklida bo'lishi kerak");

export const baseReportFilters = z.object({
	dateFrom: sanaSchema,
	dateTo: sanaSchema,
	employeeId: z.string().uuid().optional(),
	departmentId: z.string().uuid().optional(),
	zoneId: z.string().uuid().optional(),
});

/**
 * Oflayn/onlayn ustunlari — TT §4.15 «auditga chidamlilikning asosi».
 *
 * ⚠️ **FUNKSIYA, konstanta EMAS.** Umumiy massivni `...oflaynUstunlar`
 * bilan yoyish massivni nusxalaydi, lekin **obyektlarni emas** — to'qqiz
 * descriptorda `offlineCollected` bitta va o'sha obyekt bo'lardi.
 * Eksport yozuvchisi formatga qarab `column.width` ni moslasa
 * (`xlsx` vs `csv`), o'zgarish qolgan hisobotlarga JIMGINA tarqalardi.
 * Har chaqiruv yangi obyektlar qaytaradi; `Object.freeze` ATAYLAB
 * ishlatilmaydi — kontrakt §3.1 `columns: ReportColumn[]` ni
 * o'zgaruvchan deb e'lon qiladi va muzlatish o'sha moslashni
 * jimgina yiqitardi.
 */
const oflaynUstunlar = (): ReportColumn[] => [
	{ key: "offlineCollected", title: "Oflayn yig'ilgan (nuqta)", type: "number" },
	{ key: "lateArrived", title: "Kechikib kelgan (nuqta)", type: "number" },
	{ key: "gpsOffPoints", title: "GPS o'chiq (nuqta)", type: "number" },
	{ key: "longestGapMin", title: "Eng uzun uzilish (daq)", type: "duration" },
];

/** ⚠️ Yuqoridagi bilan bir xil sabab — funksiya, konstanta emas. */
const xodimUstunlar = (): ReportColumn[] => [
	{ key: "employeeName", title: "Xodim", type: "text", width: 28 },
	{ key: "departmentName", title: "Bo'lim", type: "text", width: 24 },
];

export const REPORT_DESCRIPTORS: Record<ReportKind, ReportDescriptor> = {
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti kontrakt §3.1 da snake_case — marshrut segmenti va DB qiymati bilan bir xil
	attendance_daily: {
		kind: "attendance_daily",
		title: "Kunlik davomat",
		description: "Kun bo'yicha kelish, ketish, kechikish va oflayn ulushi",
		scopeDimension: "employee",
		filters: baseReportFilters,
		columns: [
			{ key: "workDate", title: "Sana", type: "date" },
			...xodimUstunlar(),
			{ key: "status", title: "Holat", type: "text" },
			{ key: "firstCheckIn", title: "Birinchi kelish", type: "datetime" },
			{ key: "lastCheckOut", title: "Oxirgi ketish", type: "datetime" },
			{ key: "lateMin", title: "Kechikish (daq)", type: "duration" },
			{ key: "earlyLeaveMin", title: "Erta ketish (daq)", type: "duration" },
			...oflaynUstunlar(),
		],
		maxRows: 100_000,
	},
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti kontrakt §3.1 da snake_case — marshrut segmenti va DB qiymati bilan bir xil
	attendance_monthly: {
		kind: "attendance_monthly",
		title: "Oylik davomat (jamlanma)",
		description: "Xodim kesimida oy bo'yicha jamlangan ko'rsatkichlar",
		scopeDimension: "employee",
		filters: baseReportFilters,
		columns: [
			...xodimUstunlar(),
			{ key: "workDays", title: "Ish kunlari", type: "number" },
			{ key: "presentDays", title: "Kelgan kunlar", type: "number" },
			{ key: "lateDays", title: "Kechikkan kunlar", type: "number" },
			{ key: "absentDays", title: "Kelmagan kunlar", type: "number" },
			{ key: "excusedDays", title: "Uzrli kunlar", type: "number" },
			{ key: "totalLateMin", title: "Jami kechikish (daq)", type: "duration" },
		],
		maxRows: 50_000,
	},
	lateness: {
		kind: "lateness",
		title: "Kechikishlar",
		description: "Kechikish hodisalari, soni va o'rtacha vaqti",
		scopeDimension: "employee",
		filters: baseReportFilters,
		columns: [
			{ key: "workDate", title: "Sana", type: "date" },
			...xodimUstunlar(),
			{ key: "shiftStart", title: "Smena boshlanishi", type: "datetime" },
			{ key: "firstCheckIn", title: "Birinchi kelish", type: "datetime" },
			{ key: "lateMin", title: "Kechikish (daq)", type: "duration" },
		],
		maxRows: 100_000,
	},
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti kontrakt §3.1 da snake_case — marshrut segmenti va DB qiymati bilan bir xil
	zone_exits: {
		kind: "zone_exits",
		title: "Hududdan chiqishlar",
		description: "Kim, qachon, qancha vaqtga chiqdi; ruxsatli yoki ruxsatsiz",
		scopeDimension: "employee",
		filters: baseReportFilters,
		columns: [
			{ key: "occurredAt", title: "Chiqish vaqti", type: "datetime" },
			...xodimUstunlar(),
			{ key: "zoneName", title: "Hudud", type: "text", width: 24 },
			{ key: "durationMin", title: "Davomiyligi (daq)", type: "duration" },
			{ key: "permitted", title: "Ruxsatli", type: "boolean" },
			{ key: "permitReason", title: "Sabab", type: "text", width: 32 },
		],
		maxRows: 100_000,
	},
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti kontrakt §3.1 da snake_case — marshrut segmenti va DB qiymati bilan bir xil
	fake_location: {
		kind: "fake_location",
		title: "Fake location",
		description: "Mock provayder va jailbreak aniqlangan hodisalar",
		scopeDimension: "employee",
		filters: baseReportFilters,
		columns: [
			{ key: "occurredAt", title: "Vaqti", type: "datetime" },
			...xodimUstunlar(),
			{ key: "kind", title: "Turi", type: "text" },
			{ key: "severity", title: "Darajasi", type: "text" },
			{
				key: "actionTaken",
				title: "Qabul qilingan chora",
				type: "text",
				width: 32,
			},
		],
		maxRows: 50_000,
	},
	// biome-ignore lint/style/useNamingConvention: hisobot kaliti kontrakt §3.1 da snake_case — marshrut segmenti va DB qiymati bilan bir xil
	work_time: {
		kind: "work_time",
		title: "Ish vaqti",
		description: "Ishda, hududda va tashqarida o'tkazilgan vaqt",
		scopeDimension: "employee",
		filters: baseReportFilters,
		columns: [
			{ key: "workDate", title: "Sana", type: "date" },
			...xodimUstunlar(),
			{ key: "workedMin", title: "Ishda (daq)", type: "duration" },
			{ key: "insideZoneMin", title: "Hududda (daq)", type: "duration" },
			{ key: "outsideZoneMin", title: "Tashqarida (daq)", type: "duration" },
			...oflaynUstunlar(),
		],
		maxRows: 100_000,
	},
	violations: {
		kind: "violations",
		title: "Qoidabuzarliklar",
		description: "Barcha shubha hodisalari turlari bo'yicha",
		scopeDimension: "employee",
		filters: baseReportFilters,
		columns: [
			{ key: "occurredAt", title: "Vaqti", type: "datetime" },
			...xodimUstunlar(),
			{ key: "kind", title: "Turi", type: "text" },
			{ key: "severity", title: "Darajasi", type: "text" },
			{ key: "deviceName", title: "Qurilma", type: "text", width: 20 },
		],
		maxRows: 100_000,
	},
	tasks: {
		kind: "tasks",
		title: "Topshiriqlar",
		description: "Topshiriqlar, ijrochilar va yetib borish holati",
		scopeDimension: "department",
		filters: baseReportFilters,
		columns: [
			{ key: "createdAt", title: "Yaratilgan", type: "datetime" },
			// ⚠️ `title` kaliti `tasks.name` dan, `dueAt` esa `tasks.dueTo` dan
			// olinadi (kontrakt §2.2) — ataylab.
			{ key: "title", title: "Topshiriq", type: "text", width: 32 },
			{ key: "assigneeName", title: "Ijrochi", type: "text", width: 24 },
			{ key: "status", title: "Holat", type: "text" },
			{ key: "dueAt", title: "Muddat", type: "datetime" },
			{ key: "arrivedAt", title: "Yetib borgan", type: "datetime" },
			{ key: "targetCount", title: "Manzillar", type: "number" },
		],
		maxRows: 50_000,
	},
	leads: {
		kind: "leads",
		title: "Lidlar",
		description: "Qabul qilingan lidlar, forma va joylashuv kesimida",
		scopeDimension: "department",
		filters: baseReportFilters,
		columns: [
			{ key: "occurredAt", title: "Vaqti", type: "datetime" },
			{ key: "employeeName", title: "Kim kiritdi", type: "text", width: 24 },
			{ key: "formTitle", title: "Forma", type: "text", width: 28 },
			{ key: "zoneName", title: "Aniqlangan hudud", type: "text", width: 24 },
			{ key: "offlineSynced", title: "Oflayn sinxronlangan", type: "boolean" },
		],
		maxRows: 100_000,
	},
};

export const REPORT_KINDS = Object.keys(REPORT_DESCRIPTORS) as ReportKind[];

export function getReportDescriptor(kind: string): ReportDescriptor | null {
	if (Object.hasOwn(REPORT_DESCRIPTORS, kind)) {
		return REPORT_DESCRIPTORS[kind as ReportKind];
	}
	return null;
}
